import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'

const categorySchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Category name must be at least 2 characters.')
    .max(100, 'Category name cannot exceed 100 characters.'),

  description: z
    .string()
    .trim()
    .superRefine((val, ctx) => {
      if (!val || val.length === 0) return;
      if (val.length > 200) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Description cannot exceed 200 characters.',
        });
      } else if (val.length < 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Description must be at least 10 characters or left empty.',
        });
      }
    })
    .optional(),

  image: z
    .instanceof(File)
    .optional()
    .nullable(),

  displayOrder: z
    .number()
    .int('Display order must be a whole number.')
    .min(0, 'Display order must be 0 or greater.')
    .optional(),

  isActive: z.boolean().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  initialData?: {
    id?: string;
    name: string;
    slug: string;
    description?: string;
    image?: string;
    displayOrder: number;
    isActive: boolean;
  };
  onSubmit: (formData: FormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function CategoryForm({ initialData, onSubmit, onCancel, isLoading }: CategoryFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch,
    reset,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      displayOrder: initialData?.displayOrder ?? 0,
      isActive: initialData?.isActive ?? true,
    },
  });

  const [imagePreview, setImagePreview] = React.useState<string>(initialData?.image || '');
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = React.useState(false);
  const imageInputRef = React.useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Only JPG, PNG, and WebP images are supported');
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageRemoved(true);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleFormSubmit = async (data: CategoryFormData) => {
    try {
      // Image validation: Image must always be present (either existing or newly uploaded)
      const hasExistingImage = !!initialData?.image;
      const hasNewImage = !!imageFile;
      const imageWillBeRemoved = imageRemoved;

      // Final state: will there be an image after submission?
      const willHaveImage = (hasExistingImage && !imageWillBeRemoved) || hasNewImage;

      if (!willHaveImage) {
        setError('image' as any, {
          type: 'manual',
          message: 'Category image is required.',
        });
        return;
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description || '');
      formData.append('displayOrder', String(data.displayOrder || 0));
      formData.append('active', String(data.isActive !== false));

      if (imageFile) {
        formData.append('image', imageFile);
      } else if (imageRemoved) {
        formData.append('image', '');
      }

      await onSubmit(formData);
      reset();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, message]) => {
          setError(field as any, {
            type: 'server',
            message: String(message),
          });
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="space-y-6">
      {/* Category Name */}
      <div>
        <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
          Category Name *
        </label>
        <input
          type="text"
          placeholder="Enter category name"
          {...register('name')}
          className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${
            errors.name ? 'border-red-500' : 'border-[#F6EEE7]'
          }`}
        />
        {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
          Description
        </label>
        <textarea
          placeholder="Enter category description (at least 10 characters)"
          {...register('description')}
          rows={4}
          className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${
            errors.description ? 'border-red-500' : 'border-[#F6EEE7]'
          }`}
        />
        {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description.message}</p>}
      </div>

      {/* Display Order */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
            Display Order
          </label>
          <input
            type="number"
            min="0"
            step="1"
            {...register('displayOrder', {
              valueAsNumber: true,
              setValueAs: (value) => value === '' ? 0 : Number(value)
            })}
            className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${
              errors.displayOrder ? 'border-red-500' : 'border-[#F6EEE7]'
            }`}
          />
          {errors.displayOrder && <p className="text-xs text-red-600 mt-1">{errors.displayOrder.message}</p>}
        </div>

        {/* Active Status */}
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('isActive')}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-semibold text-[#6D3A0D]">Active</span>
          </label>
        </div>
      </div>

      {/* Category Image */}
      <div>
        <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
          Category Image *
        </label>
        <div className={`space-y-2 border-2 rounded-lg p-4 ${errors.image ? 'border-red-500 bg-red-50' : 'border-[#F6EEE7]'}`}>
          {imagePreview && (
            <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-[#F6EEE7]">
              <img
                src={imagePreview}
                alt="Category preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded hover:bg-red-600"
              >
                <X size={16} />
              </button>
            </div>
          )}
          <div className="border-2 border-dashed border-[#F6EEE7] rounded-lg p-4 text-center hover:border-[#6D3A0D] transition cursor-pointer">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageChange}
              className="hidden"
              id="category-image-input"
            />
            <label htmlFor="category-image-input" className="flex flex-col items-center gap-2 cursor-pointer">
              <Upload size={24} className="text-[#6D3A0D]" />
              <span className="text-sm font-semibold text-[#6D3A0D]">Click to upload image</span>
              <span className="text-xs text-gray-500">JPG, PNG, WebP up to 5MB</span>
            </label>
          </div>
        </div>
        {errors.image && <p className="text-xs text-red-600 mt-1">{errors.image.message}</p>}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting || isLoading}
          className="flex-1 px-4 py-2 border-2 border-[#F6EEE7] text-[#6D3A0D] rounded-lg font-semibold hover:bg-[#F6EEE7] transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || isLoading}
          className="flex-1 px-4 py-2 bg-[#6D3A0D] text-white rounded-lg font-semibold hover:bg-[#5D2A0D] transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting || isLoading ? 'Saving...' : 'Save Category'}
        </button>
      </div>
    </form>
  );
}
